import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Nav } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { additionalMenuItems, defaultMenuItems } from '../../config/menu';
import { useWhatsAppData } from '../../hooks/useWhatsAppData';
import NavItem from './NavItem';

/** Environment configuration for white label */
const WHITE_LABEL_PERKESO = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

/**
 * Custom hook to generate menu items based on WhatsApp configuration and user role
 * @param {Object} waCombinedData - WhatsApp configuration data
 * @param {string} userRole - User role (user, agent, manager, admin)
 * @param {boolean} isManager - Whether the user is a manager
 * @returns {Array} Combined array of menu items
 */
const useMenuItems = (waCombinedData, userRole, isManager) => {
  return useMemo(() => {
    const items = [...defaultMenuItems];

    items.push(...additionalMenuItems);

    // Add WhatsApp menu item if not in WHITE_LABEL_PERKESO mode and user has appropriate permissions
    if (!WHITE_LABEL_PERKESO && (userRole === 'user' || userRole === 'agent' || userRole === 'manager' || userRole === 'admin')) {
      const basePath = isManager ? '/manager/dashboard/whatsapp' : '/dashboard/whatsapp';
      const activePathCheck = isManager ? '/manager/dashboard/whatsapp' : '/dashboard/whatsapp';

      items.splice(defaultMenuItems.length, 0, {
        path: basePath,
        icon: 'WhatsAppLogo',
        label: 'WhatsApp',
        isActiveCheck: (pathname) => pathname.startsWith(activePathCheck),
      });
    }

    return items;
  }, [waCombinedData, userRole, isManager]);
};

/**
 * Sidebar navigation items component
 * @param {Object} props - Component props
 * @param {boolean} props.sidebarShow - Controls sidebar visibility
 */
const SidebarItems = ({ sidebarShow }) => {
  const { pathname } = useLocation();
  const { access_token, role } = useSelector((state) => state.auth.user);
  const { data: waCombinedData, error } = useWhatsAppData('get-combined-data', access_token?.token);
  const isManager = role === 'manager';
  const items = useMenuItems(waCombinedData, role, isManager);

  if (error) {
    console.error('Error in WhatsApp data fetch:', error);
  }

  const isItemActive = (item) => {
    return !item.external && (item.isActiveCheck?.(pathname) ?? pathname === item.path);
  };

  return (
    <Nav as="ul" className="responsive-scroll">
      {items.map((item) => (
        <NavItem
          key={item.path}
          item={item}
          isActive={isItemActive(item)}
          sidebarShow={sidebarShow}
        />
      ))}
    </Nav>
  );
};

SidebarItems.propTypes = {
  sidebarShow: PropTypes.bool.isRequired,
};

export default SidebarItems;
